import './referrals.scss';
import { Button, DataGrid, Form, Tooltip } from 'devextreme-react';
import { Column, Editing, Item, Paging, SearchPanel, Toolbar, Button as Butt, Selection, FilterRow, Pager } from 'devextreme-react/data-grid';
import { AddIcon, filter, resButtonRefresh, ordForwardedYes } from '../../images/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormPopup, newToast } from '../../components';
import { Item as ItemF } from 'devextreme-react/form';
import moment from 'moment';
import { confirm } from 'devextreme/ui/dialog';
import RemisionesAPI from '../../api/api.remmisions';
import { SysIRemisiones, ETypeRemission, EstadoRemisionLookup } from '../../types/types.remissions';
import { ModalCreateRemision } from './Modals/ModalCreateRemision';
import { ModalViewRemision } from './Modals/ModalViewRemision';
import { useAppSelector } from '../../contexts/hooks';
import { ModalEditRemision } from './Modals/ModalEditRemision';
import { ModalReportRemision } from '../../components/library/reporting/ModalReportRemision';
import { ModalTemporalRemissions } from './Modals/ModalTemporalRemissions';

const currentFilters = {
  startDate: moment().format('YYYY-MM-DD'),
  endDate: moment().format('YYYY-MM-DD'),
  laboratorioId: null,
  estado: null,
};

type SelectedItem = {
  [key: string]: any;
};

// Tipo para los datos de remisión
type RemisionData = SysIRemisiones | any;

export default () => {
  const gridRef = useRef<DataGrid>(null);

  // Estados para modales
  const [modalFiltersVisible, setModalFiltersVisible] = useState(false);
  const [modalCreateVisible, setModalCreateVisible] = useState(false);
  const [modalViewVisible, setModalViewVisible] = useState(false);
  const [modalEditVisible, setModalEditVisible] = useState(false);
  const [modalTemporalVisible, setModalTemporalVisible] = useState(false);
  const [selectedRemisionForEdit, setSelectedRemisionForEdit] = useState<number | null>(null);
  const [visibleReport, setVisibleReport] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);

  // Estados para datos
  const [selectedRemisionId, setSelectedRemisionId] = useState<number | null>(null);
  const [dataSource, setDataSource] = useState<RemisionData[]>([]);
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);

  const { getRemisiones, deleteRemision } = RemisionesAPI();

  // Obtener laboratorios desde Redux
  const laboratorios = useAppSelector((state) => state.configuration.Data?.ComboBox?.find((combo) => combo.Name === 'SysA_LaboratoriosRemisiones')?.Data || []);

  // Estados para los datos de lookup
  const estadosRemision: EstadoRemisionLookup[] = [
    { id: ETypeRemission.CREADA, nombre: 'CREADA' },
    { id: ETypeRemission.IMPRESA, nombre: 'IMPRESA' },
    { id: ETypeRemission.ENVIADA, nombre: 'ENVIADA' },
    { id: ETypeRemission.EN_PROCESO, nombre: 'EN PROCESO' },
    { id: ETypeRemission.COMPLETADA, nombre: 'COMPLETADA' },
    { id: ETypeRemission.CANCELADA, nombre: 'CANCELADA' },
  ];

  // Función para cargar datos
  const fetchData = async (startDate: string, endDate: string) => {
    gridRef.current?.instance?.beginCustomLoading('Cargando remisiones...');
    try {
      const fechaInicio = new Date(startDate);
      const fechaFin = new Date(endDate);
      const result = await getRemisiones(fechaInicio, fechaFin, currentFilters.estado, currentFilters.laboratorioId, null);

      // Asegurar que siempre se establezca un array
      if (result && Array.isArray(result)) {
        setDataSource(result);
      } else {
        setDataSource([]);
      }
    } catch (error) {
      newToast('ERROR', 'Error al cargar las remisiones', 4);
      setDataSource([]);
    } finally {
      gridRef.current?.instance?.endCustomLoading();
    }
  };

  // useEffect solo para carga inicial
  useEffect(() => {
    fetchData(currentFilters.startDate, currentFilters.endDate);
  }, []);

  // Event handlers con useCallback
  const handleUpdateData = useCallback(() => {
    setModalFiltersVisible(false);
    fetchData(currentFilters.startDate, currentFilters.endDate);
  }, []);

  const handleNewClick = useCallback(() => {
    setModalCreateVisible(true);
  }, []);

  const handleTemporalRemissionsClick = useCallback(() => {
    setModalTemporalVisible(true);
  }, []);

  const handleModalShowFilter = useCallback(() => {
    setModalFiltersVisible(!modalFiltersVisible);
  }, [modalFiltersVisible]);

  // Handler para ver remisión - IMPLEMENTACIÓN COMPLETA
  const handleView = useCallback((e: any) => {
    try {
      const remisionData = e.row.data;
      const remisionId = remisionData?.Rm_Id;

      // Validaciones
      if (!remisionData) {
        newToast('ERROR', 'No se pudo obtener los datos de la remisión', 4);
        return;
      }

      if (!remisionId || remisionId <= 0) {
        newToast('ERROR', 'ID de remisión no válido', 4);
        return;
      }

      // Establecer el ID y abrir el modal
      setSelectedRemisionId(remisionId);
      setModalViewVisible(true);
    } catch (error) {
      console.error('Error en handleView:', error);
      newToast('ERROR', 'Error al abrir la vista de remisión', 4);
    }
  }, []);

  const handleEdit = useCallback((e: any) => {
    try {
      const remisionData = e.row.data;
      const remisionId = remisionData?.Rm_Id;
      const estado = remisionData?.Rm_Estado;

      // Validaciones
      if (!remisionData) {
        newToast('ERROR', 'No se pudo obtener los datos de la remisión', 4);
        return;
      }

      if (!remisionId || remisionId <= 0) {
        newToast('ERROR', 'ID de remisión no válido', 4);
        return;
      }

      // Validar que se puede editar (solo estados CREADA)
      if (estado !== ETypeRemission.CREADA) {
        newToast('WARNING', 'Solo se pueden editar remisiones en estado CREADA', 4);
        return;
      }

      // Establecer el ID y abrir el modal
      setSelectedRemisionForEdit(remisionId);
      setModalEditVisible(true);
    } catch (error) {
      console.error('Error en handleEdit:', error);
      newToast('ERROR', 'Error al abrir la edición de remisión', 4);
    }
  }, []);

  const handleSelectItems = useCallback((e: any) => {
    setSelectedItems(e.selectedRowsData || []);
  }, []);

  // Manejo del cierre de modal de creación
  const handleCloseCreateModal = useCallback((refresh?: boolean) => {
    setModalCreateVisible(false);
    if (refresh) {
      // Refrescar los datos si se creó una nueva remisión
      fetchData(currentFilters.startDate, currentFilters.endDate);
      //newToast('SUCCESS', 'Datos actualizados', 2);
    }
  }, []);

  // Manejo del cierre de modal de vista
  const handleCloseViewModal = useCallback(() => {
    setModalViewVisible(false);
    setSelectedRemisionId(null);
  }, []);

  // Manejo del cierre de modal de edición - NUEVA IMPLEMENTACIÓN
  const handleCloseEditModal = useCallback((refresh?: boolean) => {
    setModalEditVisible(false);
    setSelectedRemisionForEdit(null);

    if (refresh) {
      // Refrescar los datos si se editó una remisión
      fetchData(currentFilters.startDate, currentFilters.endDate);
      //newToast('SUCCESS', 'Datos actualizados', 2);
    }
  }, []);

  // Manejo del cierre de modal de remisiones temporales
  const handleCloseTemporalModal = useCallback((refresh?: boolean) => {
    setModalTemporalVisible(false);
    if (refresh) {
      // Opcionalmente refrescar si es necesario
      // fetchData(currentFilters.startDate, currentFilters.endDate);
    }
  }, []);

  const handleClicPrint = useCallback((e) => {
    const remisionId = e.row.data.Rm_Id;

    // Validar que el ID sea válido
    if (!remisionId) {
      newToast('ERROR', 'No se puede imprimir esta remisión', 4);
      return;
    }

    // Solo establecer el ID, NO sobrescribir dataSource
    setSelectedRemisionId(remisionId);
    setVisibleReport(true);
  }, []);

  const handleDelete = useCallback(
    async (e: any) => {
      try {
        const remisionData = e.row.data;
        const remisionId = remisionData?.Rm_Id;
        const estado = remisionData?.Rm_Estado;

        // Validaciones
        if (!remisionData) {
          newToast('ERROR', 'No se pudo obtener los datos de la remisión', 4);
          return;
        }

        if (!remisionId || remisionId <= 0) {
          newToast('ERROR', 'ID de remisión no válido', 4);
          return;
        }

        // Validar que se puede eliminar (solo estados CREADA)
        if (estado !== ETypeRemission.CREADA) {
          newToast('WARNING', 'Solo se pueden eliminar remisiones en estado CREADA', 4);
          return;
        }

        // Confirmar eliminación
        const result = await confirm(
          `¿Está seguro que desea eliminar la remisión #${remisionId}?      
            Esta acción no se puede deshacer.`,
          'Confirmar eliminación'
        );

        if (!result) {
          return; // El usuario canceló
        }

        // Llamar a la API para eliminar
        const success = await deleteRemision(remisionId, setDeleteLoading, () => {
          // Callback de éxito - refrescar los datos
          fetchData(currentFilters.startDate, currentFilters.endDate);
        });

        if (success) {
          // El éxito ya se maneja en la función deleteRemision
          console.log('Remisión eliminada exitosamente');
        }
      } catch (error) {
        console.error('Error en handleDelete:', error);
        newToast('ERROR', 'Error al eliminar la remisión', 4);
        setDeleteLoading(false);
      }
    },
    [currentFilters.startDate, currentFilters.endDate, fetchData]
  );

  // También actualiza el handleCloseModalPrint para limpiar el ID
  const handleCloseModalPrint = useCallback(() => {
    setVisibleReport(false);
    setSelectedRemisionId(null);
  }, []);

  // Renderizado de estado
  const renderEstado = (cellData: any) => {
    const estado = estadosRemision.find((e) => e.id === cellData.value);
    const colorClass = getEstadoColorClass(cellData.value);

    return <span className={`status-badges ${colorClass}`}>{estado?.nombre || 'DESCONOCIDO'}</span>;
  };

  const getEstadoColorClass = (estadoId: number): string => {
    switch (estadoId) {
      case ETypeRemission.CREADA:
        return 'status-creada';
      case ETypeRemission.IMPRESA:
        return 'status-impresa';
      case ETypeRemission.ENVIADA:
        return 'status-enviada';
      case ETypeRemission.EN_PROCESO:
        return 'status-proceso';
      case ETypeRemission.COMPLETADA:
        return 'status-completada';
      case ETypeRemission.CANCELADA:
        return 'status-cancelada';
      default:
        return 'status-default';
    }
  };

  // Renderizado de cantidad de estudios
  const renderEstudios = (cellData: any) => {
    const cantidad = cellData.data.DetalleRemisiones?.length || 0;
    const urgentes = cellData.data.DetalleRemisiones?.filter((d: any) => d.Rmd_Urgente)?.length || 0;

    return (
      <div className='estudios-cell'>
        <span className='cantidad-total'>{cantidad}</span>
        {urgentes > 0 && (
          <span className='urgente-indicator' title={`${urgentes} urgente(s)`}>
            ⚡
          </span>
        )}
      </div>
    );
  };

  // Renderizado de laboratorio
  const renderLaboratorio = (cellData: any) => {
    const laboratorio = cellData.data.LaboratorioRemision;
    if (!laboratorio) return <span className='text-muted'>Sin laboratorio</span>;

    return (
      <div className='laboratorio-cell'>
        <div className='laboratorio-nombre'>{laboratorio.Lr_Nombre}</div>
        {laboratorio.Lr_Telefono && <div className='laboratorio-telefono text-muted'>{laboratorio.Lr_Telefono}</div>}
      </div>
    );
  };

  // Función para validar el formulario
  const validateForm = useCallback(() => {
    const { startDate, endDate } = currentFilters;

    // Validar que la fecha de inicio no sea mayor que la fecha fin
    if (new Date(startDate) > new Date(endDate)) {
      newToast('WARNING', 'La fecha inicial no puede ser mayor que la fecha final', 3);
      return false;
    }

    return true;
  }, [currentFilters]);

  // Función mejorada para aplicar filtros
  const handleApplyFilters = useCallback(() => {
    if (!validateForm()) {
      return;
    }

    setModalFiltersVisible(false);
    fetchData(currentFilters.startDate, currentFilters.endDate);
  }, [validateForm]);

  useEffect(() => {
    validateForm();
  }, [validateForm]);

  // Funciones auxiliares para validación de acciones
  const canEdit = useCallback((rowData: any) => {
    return rowData?.Rm_Estado === ETypeRemission.CREADA;
  }, []);

  const canDelete = useCallback((rowData: any) => {
    return rowData?.Rm_Estado === ETypeRemission.CREADA;
  }, []);

  return (
    <div className={'referrals-view'}>
      <div className={'view-wrapper view-wrapper-contact-list'}>
        <DataGrid className='grid' dataSource={dataSource} ref={gridRef} keyExpr='Rm_Id' height='100%' width={'100%'} hoverStateEnabled={true} repaintChangesOnly={true} onSelectionChanged={handleSelectItems} highlightChanges={false} rowAlternationEnabled={true} showColumnLines={true} columnAutoWidth={true} showBorders={true}>
          <Editing allowUpdating={false} allowDeleting={true} />
          <FilterRow visible={true} applyFilter='auto' />
          <SearchPanel visible placeholder='Buscar remisiones...' width={300} />
          {/* <Selection mode='multiple' /> */}
          <Paging enabled={true} />
          <Pager visible={true} allowedPageSizes={[20, 50, 100, 'all']} displayMode='full' showPageSizeSelector={true} showInfo={true} showNavigationButtons={true} />
          <Toolbar>
            <Item location='before'>
              <div className='grid-header'>
                <h2>Remisiones</h2>
                {selectedItems.length > 0 && <span className='selection-info'>({selectedItems.length} seleccionadas)</span>}
              </div>
            </Item>
            <Item location='before' locateInMenu='auto'>
              <div className='separator' />
            </Item>
            <Item location='before' locateInMenu='auto' showText='inMenu' widget='dxButton'>
              <Tooltip target='#toolBarTem' showEvent='mouseenter' hideEvent='mouseleave' position='top'>
                <div>
                  <strong>Remisiones temporales</strong>
                </div>
              </Tooltip>
              <Button id='toolBarTem' text='Remisiones temporales' stylingMode='contained' icon={ordForwardedYes} onClick={handleTemporalRemissionsClick} />
            </Item>
            <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
              <Tooltip target='#toolBarNew' showEvent='mouseenter' hideEvent='mouseleave' position='top'>
                <div>
                  <strong>Nueva remisión</strong>
                </div>
              </Tooltip>
              <Button id='toolBarNew' text='Nueva Remisión' stylingMode='contained' icon={AddIcon} onClick={handleNewClick} />
            </Item>
            <Item location='after' locateInMenu='auto'>
              <div className='separator' />
            </Item>
            <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
              <Tooltip target='.filter-button' showEvent='mouseenter' hideEvent='mouseleave' position='top'>
                Filtrar remisiones
              </Tooltip>
              <Button className='filter-button' icon={filter} text='Filtros' stylingMode='text' onClick={handleModalShowFilter} />
            </Item>
            <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
              <Tooltip target='.refresh-button' showEvent='mouseenter' hideEvent='mouseleave' position='top'>
                Actualizar lista de remisiones
              </Tooltip>
              <Button className='refresh-button' icon={resButtonRefresh} text='Actualizar' stylingMode='text' onClick={handleUpdateData} />
            </Item>
            <Item name='searchPanel' locateInMenu='auto' />
          </Toolbar>

          <Column dataField='Rm_Id' caption='NÚMERO' alignment='center' width={90} cellRender={(cellData) => cellData.value} />
          <Column dataField='Rm_FechaCreacion' caption='FECHA CREACIÓN' dataType='datetime' format='dd/MM/yyyy HH:mm' alignment='center' width={140} />
          <Column dataField='LaboratorioRemision' caption='LABORATORIO' cellRender={renderLaboratorio} alignment='left' width={200} />
          <Column dataField='Usuario.Us_Nombre' caption='CREADO POR' alignment='center' width={150} />
          <Column dataField='Rm_Estado' caption='ESTADO' cellRender={renderEstado} alignment='center' width={150} />
          <Column caption='ESTUDIOS' cellRender={renderEstudios} alignment='center' width={100} allowSorting={false} />
          <Column dataField='Rm_FechaEnvio' caption='FECHA ENVÍO' dataType='datetime' format='dd/MM/yyyy HH:mm' alignment='center' width={140} cellRender={(cellData) => (cellData.value ? <span>{moment(cellData.value).format('DD/MM/YYYY HH:mm')}</span> : <span className='text-muted'>Sin enviar</span>)} />
          <Column dataField='Rm_ResponsableEnvio' caption='REMITE' alignment='left' width={150} cellRender={(cellData) => cellData.value || <span className='text-muted'>No asignado</span>} />
          <Column type='buttons' caption='ACCIONES' width={120} fixedPosition='right'>
            <Butt icon='eyeopen' text='Ver' hint='Ver remisión' onClick={handleView} />
            <Butt icon='edit' text='Editar' hint='Editar remisión' onClick={handleEdit} disabled={(e) => !canEdit(e.row.data)} />
            <Butt icon='trash' text='Eliminar' hint='Eliminar remisión' onClick={handleDelete} />
            <Butt icon='print' text='Imprimir' hint='Imprimir remisión' onClick={handleClicPrint} />
            {/* <Butt icon='send' text='Enviar' hint='Enviar remisión' onClick={handleClicPrint} /> */}
          </Column>
        </DataGrid>
      </div>

      {/* Modal de filtros */}
      {modalFiltersVisible && (
        <FormPopup title='Filtrar Remisiones' visible={modalFiltersVisible} setVisible={handleModalShowFilter} onSave={handleApplyFilters} loading={false} allowSave={false}>
          <Form formData={currentFilters} labelLocation='top' className='plain-styled-form'>
            <ItemF
              dataField='startDate'
              editorType='dxDateBox'
              isRequired={true}
              label={{ text: 'Fecha inicial *' }}
              validationRules={[{ type: 'required', message: 'La fecha inicial es requerida' }]}
              editorOptions={{
                stylingMode: 'outlined',
                showClearButton: true,
                max: currentFilters.endDate,
              }}
            />
            <ItemF
              dataField='endDate'
              editorType='dxDateBox'
              isRequired={true}
              label={{ text: 'Fecha final *' }}
              validationRules={[{ type: 'required', message: 'La fecha final es requerida' }]}
              editorOptions={{
                stylingMode: 'outlined',
                showClearButton: true,
                min: currentFilters.startDate,
              }}
            />
            <ItemF
              dataField='laboratorioId'
              editorType='dxSelectBox'
              label={{ text: 'Laboratorio' }}
              editorOptions={{
                dataSource: laboratorios,
                displayExpr: 'Lr_Nombre',
                valueExpr: 'Lr_Id',
                placeholder: 'Todos los laboratorios',
                showClearButton: true,
                stylingMode: 'outlined',
                searchEnabled: true,
              }}
            />
            <ItemF
              dataField='estado'
              editorType='dxSelectBox'
              label={{ text: 'Estado' }}
              editorOptions={{
                dataSource: estadosRemision,
                displayExpr: 'nombre',
                valueExpr: 'id',
                placeholder: 'Todos los estados',
                showClearButton: true,
                stylingMode: 'outlined',
              }}
            />
          </Form>
        </FormPopup>
      )}

      {/* Modal de crear remisión */}
      <ModalCreateRemision visible={modalCreateVisible} onClose={handleCloseCreateModal} />
      <ModalReportRemision visible={visibleReport} setVisible={handleCloseModalPrint} reportName='Remisiones' idRemision={selectedRemisionId ? selectedRemisionId : -1} />

      {/* Modal de ver remisión - IMPLEMENTACIÓN COMPLETA */}
      {modalViewVisible && selectedRemisionId && <ModalViewRemision visible={modalViewVisible} remisionId={selectedRemisionId} onClose={handleCloseViewModal} wrapperAttr={{ class: 'view-remision-modal' }} />}
      {modalEditVisible && selectedRemisionForEdit && <ModalEditRemision visible={modalEditVisible} remisionId={selectedRemisionForEdit} onClose={handleCloseEditModal} wrapperAttr={{ class: 'edit-remision-modal' }} />}

      {/* Modal de remisiones temporales */}
      <ModalTemporalRemissions visible={modalTemporalVisible} onClose={handleCloseTemporalModal} />
    </div>
  );
};
